import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PortfolioMovementService, PortfolioMovementType } from '../../../services/portfolio-movement.service';
import { CorporatePortfolioMovementService } from '../../../services/corporate-portfolio-movement.service';
import { LoadingService } from '../../../services/loading.service';
import { ProjectService } from '../../../services/project.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-portfolio-movements-list',
  templateUrl: './portfolio-movements-list.component.html',
  styleUrls: ['./portfolio-movements-list.component.scss']
})
export class PortfolioMovementsListComponent implements OnInit, OnDestroy {
  view: 'individual' | 'corporate' = 'individual';
  movementsData: any[] = [];

  // Debounce
  private filterSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Filtros
  searchTerm: string = '';
  selectedMovementType: string = '';
  selectedProject: any = null;

  // Paginacion
  pageNumber: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;

  // Ordenamiento
  sortBy: string = 'movementDate';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Stats
  totalWithdrawals: number = 0;
  totalReversals: number = 0;
  totalDeferrals: number = 0;
  totalWithdrawnAmount: number = 0;
  totalReversedAmount: number = 0;
  totalDeferredAmount: number = 0;
  activeWithdrawals: number = 0;
  activeDeferrals: number = 0;

  // Proyectos para dropdown
  uniqueProjects: any[] = [];

  // Opciones de tipo de movimiento para el dropdown
  movementTypeOptions = [
    { label: 'Todos los tipos', value: '' },
    { label: 'Retiro', value: '1' },
    { label: 'Reversion de Retiro', value: '2' },
    { label: 'Aplazamiento', value: '3' },
    { label: 'Reversion de Aplazamiento', value: '4' },
    { label: 'Castigo Manual', value: '5' },
    { label: 'Descastigo Manual', value: '6' }
  ];

  constructor(
    private portfolioMovementService: PortfolioMovementService,
    private corporatePortfolioMovementService: CorporatePortfolioMovementService,
    private projectService: ProjectService,
    private loadingService: LoadingService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.filterSubject.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadData();
    });

    this.route.data.subscribe((data: any) => {
      if (data && data['view']) {
        this.switchView(data['view']);
      } else {
        this.switchView('individual');
      }
    });

    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects() {
    this.projectService.getAll().subscribe((res: any) => {
      if (res && res.data) {
        this.uniqueProjects = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      }
    });
  }

  switchView(view: 'individual' | 'corporate') {
    this.view = view;
    this.pageNumber = 1;
    this.searchTerm = '';
    this.selectedMovementType = '';
    this.selectedProject = null;
    this.loadData();
  }

  onFilterChange() {
    this.filterSubject.next();
  }

  onPageChange(event: any) {
    this.pageNumber = event.page + 1;
    this.pageSize = event.rows;
    this.loadData();
  }

  onSortChange(field: string) {
    if (this.sortBy === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortDirection = 'desc';
    }
    this.pageNumber = 1;
    this.loadData();
  }

  loadData() {
    if (this.view === 'individual') {
      this.loadIndividualMovements();
      this.loadIndividualStats();
    } else {
      this.loadCorporateMovements();
      this.loadCorporateStats();
    }
  }

  private loadIndividualMovements() {
    const params: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection
    };

    if (this.searchTerm) params.searchTerm = this.searchTerm;
    if (this.selectedMovementType) params.movementType = Number(this.selectedMovementType);
    if (this.selectedProject) params.projectId = this.selectedProject;

    this.loadingService.show();
    this.portfolioMovementService.getMovementsPaged(params).subscribe({
      next: (res: any) => {
        if (res.isSuccess) {
          this.movementsData = res.data.data || [];
          this.totalRecords = res.data.totalCount || 0;
        } else {
          this.movementsData = [];
          this.totalRecords = 0;
        }
        this.loadingService.hide();
      },
      error: () => {
        this.movementsData = [];
        this.totalRecords = 0;
        this.loadingService.hide();
      }
    });
  }

  private loadCorporateMovements() {
    const params: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection
    };

    if (this.searchTerm) params.searchTerm = this.searchTerm;
    if (this.selectedMovementType) params.movementType = Number(this.selectedMovementType);
    if (this.selectedProject) params.projectId = this.selectedProject;

    this.loadingService.show();
    this.corporatePortfolioMovementService.getCorporateMovementsPaged(params).subscribe({
      next: (res: any) => {
        if (res.isSuccess) {
          this.movementsData = res.data.data || [];
          this.totalRecords = res.data.totalCount || 0;
        } else {
          this.movementsData = [];
          this.totalRecords = 0;
        }
        this.loadingService.hide();
      },
      error: () => {
        this.movementsData = [];
        this.totalRecords = 0;
        this.loadingService.hide();
      }
    });
  }

  private loadIndividualStats() {
    this.portfolioMovementService.getMovementStats().subscribe({
      next: (res: any) => {
        if (res.isSuccess && res.data) {
          this.assignStats(res.data);
        }
      }
    });
  }

  private loadCorporateStats() {
    this.corporatePortfolioMovementService.getCorporateMovementStats().subscribe({
      next: (res: any) => {
        if (res.isSuccess && res.data) {
          this.assignStats(res.data);
        }
      }
    });
  }

  private assignStats(data: any) {
    this.totalWithdrawals = data.totalWithdrawals || 0;
    this.totalReversals = data.totalReversals || 0;
    this.totalDeferrals = data.totalDeferrals || 0;
    this.totalWithdrawnAmount = data.totalWithdrawnAmount || 0;
    this.totalReversedAmount = data.totalReversedAmount || 0;
    this.totalDeferredAmount = data.totalDeferredAmount || 0;
    this.activeWithdrawals = data.activeWithdrawals || 0;
    this.activeDeferrals = data.activeDeferrals || 0;
  }

  // Offcanvas detalle
  selectedMovement: any = null;

  openDetail(movement: any) {
    this.selectedMovement = movement;
  }

  closeDetail() {
    this.selectedMovement = null;
  }

  // Helpers

  getMovementTypeLabel(type: number): string {
    return PortfolioMovementService.getMovementTypeDescription(type as PortfolioMovementType);
  }

  getMovementTypeClass(type: number | string): string {
    const t = String(type).toLowerCase();
    // Handle both numeric and string enum values
    if (t === '1' || t === 'withdrawal') return 'bg-red-100 text-red-800';
    if (t === '2' || t === 'withdrawalreversal') return 'bg-green-100 text-green-800';
    if (t === '3' || t === 'deferral') return 'bg-orange-100 text-orange-800';
    if (t === '4' || t === 'deferralreversal') return 'bg-teal-100 text-teal-800';
    if (t === '5' || t === 'manualpunishment') return 'bg-purple-100 text-purple-800';
    if (t === '6' || t === 'manualunpunishment') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);
  }
}
